/* eslint-disable */
/**
 * Co-living underwriting — rental-hold return math for the LeadDetailV2 calculator.
 *
 * Pure math, no IO. The co-living variant of the calculator (shown when a lead ran
 * co-living analysis but no ARV/comps) works backward from INCOME, not a resale
 * price. Where the flip calc anchors on ARV, this anchors on NOI:
 *
 *   Gross rent (rooms × room rate)  →  less operating costs  =  NOI
 *   MAO = NOI ÷ target cap rate − conversion cost           (reverse cap-rate solve)
 *
 * NOI deliberately excludes debt and one-time costs, so it measures the building,
 * not the financing. Debt service is layered on afterward for the levered metrics
 * (monthly cash flow, cash-on-cash, breakeven occupancy).
 *
 * Cost knobs default from DEFAULT_CO_LIVING_UNDERWRITING (researched benchmarks for
 * rent-by-the-room / PadSplit operations) and are overridable per-lead. Taxes,
 * insurance and HOA are MONTHLY figures carried from the lead's existing
 * underwriting inputs (same convention as profitCalculation.js).
 */

// Researched defaults for rent-by-the-room / PadSplit-style co-living operations.
// Rates are decimals (0.10 = 10%); money figures are dollars; utilities/cleaning
// are MONTHLY. See the plan notes / BiggerPockets + PadSplit host data for basis.
//
// ⚠️ DO NOT RE-DECLARE THESE HERE. This file used to carry its own literal copy, which
// made a single default live in four places (this file + coLivingKnobs.js, mirrored by
// coLivingReturns.js + companyFeatures.js on the backend). Changing three of the four
// shipped green: the CI knob guard never read this file, and the jest suite that did is
// not run by CI — so the deal-page calculator could quote a buyer an economics the
// ingestion gate and the buyer matcher disagreed with.
//
// The defaults are now DERIVED from the knob table in ./coLivingKnobs (the frontend's
// single source), which the CI guard vm-evals and pins against the backend table in
// functions/utils/underwritingKnobTables.js. To change a default, edit the BACKEND table
// and mirror it in ./coLivingKnobs — CI fails if the two disagree.
import { DEFAULT_CO_LIVING_UNDERWRITING } from './coLivingKnobs';

export { DEFAULT_CO_LIVING_UNDERWRITING };

const num = (v, fallback = 0) => (typeof v === 'number' && !isNaN(v) && isFinite(v)) ? v : fallback;
const nonNeg = (v, fallback = 0) => { const n = num(v, fallback); return n >= 0 ? n : fallback; };
const round2 = (v) => Math.round(v * 100) / 100;
// Display helpers for the opex rows' `assumption` strings. One decimal place at most —
// the knobs are set as whole or half percents, and "14%" reads as the assumption it is
// where "14.0%" reads as a measurement. Kept here rather than in the UI because the
// rate and the dollar figure it produced have to stay in the same object: a row whose
// stated rate and value came from different renders is worse than stating no rate.
const pctText = (p) => `${Math.round(nonNeg(p) * 1000) / 10}%`;
const moneyText = (v) => `$${Math.round(nonNeg(v)).toLocaleString('en-US')}`;

/**
 * An assumption stated as PARTS — literal strings interleaved with tokens that name the
 * underwriting knob each number came out of — rather than only as a finished sentence.
 *
 * WHY. The rate in "Vacancy · 10% of gross rent" is the single thing a reader most wants to
 * argue with, and any editor that offers it has to know WHICH knob to write back and in what
 * unit. Recovering that by parsing the rendered sentence is the obvious shortcut and the wrong
 * one: "7.2 of 8 rooms × $60/mo" carries two numbers and only one of them is a knob (the other
 * is derived from the room count and the vacancy rate). Naming the knob where the sentence is
 * BUILT is the only place that information exists for free.
 *
 * ⚠️ `assumption` is DERIVED from these parts, never written beside them. The read-only
 * sentence and the editable one are then the same sentence by construction, so a row cannot
 * say "10% of gross rent" while its input writes to a different knob or a different unit.
 *
 * `human` is the value in the units the KNOB TABLE stores (percent as 10, not 0.1) — what an
 * input must show and what coerceCoLivingUnderwritingKnob validates — while `text` is how the
 * same value reads when nobody is editing it.
 */
const pctKnob = (knob, decimal) => ({
    knob, text: pctText(decimal), human: Math.round(nonNeg(decimal) * 1000) / 10,
});
const dollarKnob = (knob, dollars) => ({
    knob, text: moneyText(dollars), human: Math.round(nonNeg(dollars)),
});
const assumptionText = (parts) => parts.map((p) => (typeof p === 'string' ? p : p.text)).join('');

/**
 * The utilities row's `assumption` — it has to name BOTH halves of the two-part tariff, or the
 * reader can't reproduce the figure. "$400/house + 7.2 of 8 rooms × $60/mo" is checkable;
 * "8 rooms × $130/mo" (the old string) was checkable too but described a model that wasn't true.
 *
 * ⚠️ The room count here is the OCCUPIED count (beds less vacancy), not the bed count, because
 * that is what the volume charge is actually multiplied by. Printing the nominal "8 rooms" while
 * charging 7.2 would put a figure on screen the stated arithmetic does not produce — the exact
 * failure this string exists to prevent. Both counts are shown ("7.2 of 8") so the haircut is
 * visible rather than looking like a wrong bed count; at zero vacancy the two collapse to one.
 * Rounded to 1dp for display, the same tolerance pctText already takes on the percentage rows.
 *
 * A half the operator has zeroed is omitted rather than printed as "+ $0": the row states
 * assumptions, and a written zero reads as a measured absence. Both zero ⇒ the members carry
 * the utilities, which is a real configuration and deserves to be said in words.
 */
const roomsText = (occupiedRooms, beds) => {
    const occupied = nonNeg(occupiedRooms);
    // Plurality is always the BED count's, because that is the noun's actual subject in both
    // forms: "8 rooms", and "0.9 of 1 room" — not "0.9 of 1 rooms". Agreeing with the occupied
    // count instead produced "1 of 2 room" at 50% vacancy and "0.9 of 1 rooms" at 10%.
    const noun = beds === 1 ? 'room' : 'rooms';
    // Collapse ONLY at genuinely full occupancy, tested before rounding. Testing the rounded
    // value let a 0.2% vacancy print a bare "8 rooms" while the row charged 7.984 — a string
    // asserting full occupancy on a house that has a haircut, which is the precise failure
    // this whole assumption exists to prevent.
    if (occupied === beds) return `${beds} ${noun}`;
    // 1dp as elsewhere in this file, but never let rounding print the occupied count AS the bed
    // count; fall to a floored 2dp so the stated count stays strictly under the bed count.
    let n = Math.round(occupied * 10) / 10;
    if (n === beds) n = Math.floor(occupied * 100) / 100;
    return `${n} of ${beds} ${noun}`;
};

const utilitiesAssumptionParts = (k, occupiedRooms, beds) => {
    const base = nonNeg(k?.utilitiesBaseMonthly);
    const perRoom = nonNeg(k?.utilitiesPerRoomMonthly);
    // Each half is a GROUP of parts rather than a string, so the ' + ' join still happens here
    // (identical output to the old builder) but the two knob tokens survive into the array.
    const groups = [];
    if (base > 0) groups.push([dollarKnob('utilitiesBaseMonthly', base), '/house']);
    if (perRoom > 0) groups.push([`${roomsText(occupiedRooms, beds)} × `, dollarKnob('utilitiesPerRoomMonthly', perRoom), '/mo']);
    if (groups.length === 0) return ['Paid by members'];
    // ⚠️ A ZEROED HALF STAYS UNRENDERABLE, so it also stays UNEDITABLE. That is the existing
    // deliberate behaviour ("a written zero reads as a measured absence") and this change does
    // not relax it: a company that has zeroed its per-room charge cannot turn it back on from
    // the ledger, only in Settings. Same for a whole row — see the platformFee/managementFee
    // `> 0` guards below.
    return groups.length === 1 ? groups[0] : [...groups[0], ' + ', ...groups[1]];
};

/**
 * Standard fully-amortizing monthly mortgage payment.
 * @param {number} loanAmount
 * @param {number} annualRate - decimal (0.075)
 * @param {number} termYears
 * @returns {number} monthly payment (0 when no loan)
 */
export const amortizedMonthlyPayment = (loanAmount, annualRate, termYears) => {
    const P = nonNeg(loanAmount);
    if (P <= 0) return 0;
    const n = Math.max(1, Math.round(num(termYears, 30) * 12));
    const r = num(annualRate, 0) / 12;
    if (r <= 0) return round2(P / n);
    const payment = P * r / (1 - Math.pow(1 + r, -n));
    return round2(payment);
};

/**
 * Cash-to-close figures — everything the investor wires at purchase. These depend
 * ONLY on price + conversion cost + loan terms, NEVER on the rent projection, so
 * they're computed separately and ride through even when rooms/rate are missing
 * (see the early return in computeCoLivingReturns). Points scale with the LOAN, so
 * an all-cash buyer pays none. Returns all-nulls when there's no price to price off.
 */
const computeAcquisition = (purchasePrice, conversionCost, k, monthlyFixedCarry = 0) => {
    const price = (typeof purchasePrice === 'number' && purchasePrice > 0) ? purchasePrice : null;
    if (price == null) {
        return { loanAmount: null, downPaymentDollars: null, closingCosts: null, loanPoints: null, holdingCost: null, cashInvested: null };
    }
    const downPayment = Math.min(1, Math.max(0, num(k.downPayment, 0)));
    const loanAmount = round2(price * (1 - downPayment));
    const downPaymentDollars = round2(price * downPayment);
    // Flat closing OVERRIDES the percent-of-price closing when set (> 0); else percent.
    const closingFlat = nonNeg(k.closingCostFlat);
    const closingCosts = round2(closingFlat > 0 ? closingFlat : price * nonNeg(k.closingCostPct));
    const loanPoints = round2(loanAmount * nonNeg(k.loanPointsPct));
    // Holding: months of the FULL carrying payment — P&I + taxes + insurance + HOA —
    // carried during conversion. 0 months ⇒ off.
    const holdingMonths = nonNeg(k.holdingMonths);
    const holdingCost = holdingMonths > 0
        ? round2((amortizedMonthlyPayment(loanAmount, k.mortgageRate, k.loanTermYears) + nonNeg(monthlyFixedCarry)) * holdingMonths)
        : 0;
    // Holding rides in cashInvested (the cash-on-cash denominator) only — NOT the
    // cap-rate basis / MAO solve, which use price + conversion.
    const cashInvested = round2(downPaymentDollars + nonNeg(conversionCost) + closingCosts + loanPoints + holdingCost);
    return { loanAmount, downPaymentDollars, closingCosts, loanPoints, holdingCost, cashInvested };
};

/**
 * How many bedrooms the conversion premium was PRICED FOR. The premium is struck once,
 * at ingestion, against a specific projected count — it names which one in `basis` — so
 * that field is the only honest answer to "what does this dollar figure buy?".
 *
 * Needed because the analyst can now dial the conversion cards ABOVE the plan's count
 * (coLivingSpotBeds.js). A bed beyond the basis is a bed the premium never paid for.
 */
export const conversionPremiumBeds = (interior) => {
    const cl = interior || {};
    const pb = cl.projectedBedrooms || {};
    const basis = cl.conversionPremium?.basis;
    // 'roughEstimate' is the no-floor-plan premium's basis — its count lives on
    // roughBedroomEstimate.total, not projectedBedrooms (null on those docs). The
    // fallback chain below would land on grossRent.beds (= the same number) anyway;
    // resolving it explicitly keeps "premium basis names its own count" literally true.
    const fromBasis = basis === 'roughEstimate'
        ? cl.roughBedroomEstimate?.total
        : (basis ? pb[basis] : null);
    const n = num(fromBasis, NaN);
    if (isFinite(n) && n >= 0) return n;
    // Legacy docs whose `basis` field names a projection they never persisted. Fall back
    // down THE CANONICAL DEFAULT-ROOMS CHAIN — the exact one every surface uses to seed an
    // unedited lead's room count (coLivingGrossRent.js#resolveCoLivingHeadlineRent,
    // useLeadFetch#derivedRooms, LeadBuyerMatches#derivedRooms, crmSendPayload).
    //
    // These two chains MUST stay identical, and that is the whole safety property: an
    // untouched lead's room count then lands exactly ON the basis, so `beyond` is 0 and no
    // surcharge can appear on a deal nobody edited. Reorder one and not the other (an
    // earlier cut of this had `ceiling` before `grossRent.beds`) and the back catalogue
    // quietly gets billed for beds it never gained.
    const fallback = pb.ceilingWithConstruction
        ?? cl.grossRent?.beds
        ?? pb.ceiling
        ?? cl.roughBedroomEstimate?.total
        ?? null;
    const f = num(fallback, NaN);
    return (isFinite(f) && f >= 0) ? f : null;
};

/**
 * What ONE bedroom beyond the premium's basis costs. Priced from the premium's OWN
 * persisted assumptions, so an extra bed costs what this deal's plan said a bed costs —
 * not a company knob the floor-plan path never used.
 *
 * `buildOutPerBed` MIDPOINT is the right marginal line: a bed added past the plan is the
 * analyst splitting a room the reader already walked (frame walls + egress + HVAC), and we
 * take the MIDPOINT because that's the basis the calculator now consumes the premium at
 * (see computeCoLivingFurnishing, changed 2026-08-04). Mixing a mid basis with a high
 * marginal would make the 9th bed dearer than the 8th for no observable reason.
 * Legacy docs with no `assumptions` fall back to the company knobs.
 * Mirror of functions/utils/coLivingReturns.js#perExtraBedCost.
 */
const bandMid = (b) => {
    const lo = num(b?.low, NaN), hi = num(b?.high, NaN);
    if (isFinite(lo) && isFinite(hi)) return (lo + hi) / 2;
    return isFinite(hi) ? hi : NaN;   // a pinned/legacy band with only one end
};
const perExtraBedCost = (premium, k) => {
    const a = premium?.assumptions || null;
    const furnish = num(a?.perBedFurnish, NaN);
    const buildOut = bandMid(a?.buildOutPerBed);
    if (isFinite(furnish) && isFinite(buildOut)) return nonNeg(furnish) + nonNeg(buildOut);
    return nonNeg(k.furnishingPerRoom) + nonNeg(k.costPerAddedBedroom);
};

/**
 * What ONE bathroom added AFTER ingestion costs.
 *
 * The premium prices the conversion the plan drew; it never included a bath the
 * analyst decides to add later — conversionPremium.js keeps that in a SEPARATE `unlock`
 * line precisely because it's an optional decision, and the beds it frees are deliberately
 * excluded from `ceilingWithConstruction`. So charging for it here double-counts nothing:
 * the bath price is the bath, the bed price is the bed.
 *
 * It has to be charged at all because of PadSplit's own rule — no more than four members
 * per bathroom. A bath is what LIFTS the bed ceiling by maxBedsPerBath, so leaving it free
 * makes it a free lever that unlocks four beds the investor does pay for.
 *
 * Precedence: the COMPANY'S own number when they've set one (the knob defaults to 0, which
 * is "unset", not "free"), else the premium's persisted bathUnlock — this deal's own quote.
 */
const perExtraBathCost = (premium, k) => {
    const knob = nonNeg(k.costPerAddedBathroom);
    if (knob > 0) return knob;
    // Midpoint, matching perExtraBedCost and the premium's own consumption basis. On the
    // current config bathUnlock is pinned $10,000/$10,000 so this equals `.high` today.
    const fromPremium = bandMid(premium?.assumptions?.bathUnlock);
    return isFinite(fromPremium) ? nonNeg(fromPremium) : 0;
};

/**
 * How many full baths this deal is charged for.
 *
 * ⚠️ MIRROR of functions/utils/coLivingReturns.js#resolveAddedBaths — change one, change the
 * other. The frontend cannot import from `functions/`, so this is a hand copy like the rest of
 * this file.
 *
 * Until 2026-08-10 the only answer was "whatever a human typed into the deal page", so an
 * auto-ingested lead nobody had opened was priced as adding ZERO bathrooms while its bed
 * ceiling was computed from the PRE-conversion bath count. `coLiving/bathAddition.js` now
 * estimates it from the excess-space budget and it rides the property doc (and the buyer
 * projection) as `coLivingInterior.bathAddition`.
 *
 * 🚨 WITHOUT THIS MIRROR THE PAGE UNDER-CHARGES. The backend gate resolves the estimate; a
 * page still reading `calc.coLivingBathsAdded` alone shows $0 for a bathroom the strategy-fit
 * gate charged $10k for, on every lead nobody has typed into.
 *
 *   1. the analyst's own figure — the last word on this house. A typed 0 is a real answer.
 *   2. the persisted estimate, and only when it RESOLVED (a refusal is not an estimate of 0).
 *   3. zero.
 */
export const resolveAddedBaths = (coLivingInterior, calculatorInputs) => {
    const manual = calculatorInputs?.coLivingBathsAdded;
    if (typeof manual === 'number' && isFinite(manual)) return nonNeg(manual);
    const est = coLivingInterior?.bathAddition;
    if (est && typeof est === 'object' && est.resolved === true
        && typeof est.bathsAdded === 'number' && isFinite(est.bathsAdded)) {
        return Math.floor(nonNeg(est.bathsAdded));
    }
    return 0;
};

/**
 * The FURNISHING + build-out line of a rent-by-the-room conversion (the "conversion
 * premium" the deal-page calculator shows and lets the investor override). Callers
 * (deal-page calculator + buyer matcher) use THIS so their numbers agree, then add the
 * repair/inspection line on top: conversionCost = repairsOrBuffer + this.
 *   - Floor-plan deals: conversionPremium.MID prices furnishing + build-out for the beds
 *     it was struck at (`premiumBeds`), so use it verbatim UP TO that count — the
 *     per-added-cost knobs would double-count inside it. Every bed the analyst dials
 *     ABOVE that count is charged at the premium's own per-bed rate: rent and cost move
 *     together, or a click on "+" mints NOI (and MAO) out of nothing.
 *   - No-floor-plan deals: furnish every bedroom + charge per ADDED bed/bath from knobs.
 *
 * MID, NOT HIGH (changed 2026-08-04) — every premium line is a range, and consuming `high`
 * charged the ceiling on each one, five separate times on a 5-bed conversion. See
 * functions/utils/coLivingReturns.js#computeCoLivingFurnishing for the full rationale.
 *
 * `premiumBeds` (from conversionPremiumBeds) is REQUIRED to charge for extra beds; with
 * it null we can't tell what the premium covered, so we fall back to the old
 * use-it-verbatim behaviour rather than guess a surcharge.
 */
// Central estimate of a persisted premium. Docs written before 2026-08-04 carry no `mid`,
// so derive it from the stored spread. Exact in the MODEL (every term is linear, so the
// midpoint of the total is the sum of the per-line midpoints) but NOT to the dollar: `low` and
// `high` are each published rounded to `roundStep`, so averaging them can land up to
// roundStep/2 (±$250) off the `mid` the same doc would carry today. Measured, not theorised —
// e.g. a doc storing 23,000/52,500 derives 37,750 where the model's own mid is 38,000.
// Immaterial against a five-figure conversion, and it only applies to pre-2026-08-04 docs,
// which stop existing as leads are re-scored. Don't "fix" it by rounding here: that would
// make two docs of the same house disagree by more than the error it removes.
// Mirror of functions/utils/coLivingReturns.js#premiumMid.
export const premiumMid = (p) => {
    const mid = num(p?.mid, NaN);
    if (isFinite(mid)) return mid;
    const lo = num(p?.low, NaN), hi = num(p?.high, NaN);
    if (isFinite(lo) && isFinite(hi)) return (lo + hi) / 2;
    // Partial doc (a `high` with no spread): fall back to it rather than returning NaN,
    // which would drop the caller into the legacy knob formula — a route change, not a
    // basis change.
    return isFinite(hi) ? hi : NaN;
};
export const computeCoLivingFurnishing = ({ conversionPremium = null, premiumBeds = null, totalBeds = 0, addedBeds = 0, addedBaths = 0, knobs = {} }) => {
    const k = { ...DEFAULT_CO_LIVING_UNDERWRITING, ...knobs };
    // Conversion cost is OFF for every company until it opts in (coLivingKnobs.js
    // isCoLivingConversionCostOn). Gated here — the one function that prices the line on
    // this side — so a caller that forgets to thread the flag fails SAFE to $0.
    // Mirror of functions/utils/coLivingReturns.js#computeCoLivingFurnishing.
    if (!k.conversionCostOn) return 0;
    const beds = nonNeg(totalBeds);
    const mid = premiumMid(conversionPremium);
    if (isFinite(mid) && mid >= 0) {
        const basis = num(premiumBeds, NaN);
        const beyond = (isFinite(basis) && basis >= 0) ? Math.max(0, beds - basis) : 0;
        // Beds past the plan AND any bath planned after ingestion — neither is inside `mid`.
        return round2(
            mid
            + beyond * perExtraBedCost(conversionPremium, k)
            + nonNeg(addedBaths) * perExtraBathCost(conversionPremium, k)
        );
    }
    const adds = nonNeg(addedBeds);
    const baths = nonNeg(addedBaths);
    // Flat per-house setup (common-area furniture, WiFi, locks) — the legacy formula's
    // analogue of the floor-plan config's `houseSetup`, for operators who furnish the
    // house but not the bedrooms. Charged once, and only when there are rentable beds at
    // all. NEVER added on the premium branch above: houseSetup is already inside `high`.
    const house = beds > 0 ? nonNeg(k.furnishingPerHouse) : 0;
    return round2(house + beds * nonNeg(k.furnishingPerRoom) + adds * nonNeg(k.costPerAddedBedroom) + baths * nonNeg(k.costPerAddedBathroom));
};

/**
 * What the conversion-cost box SHOWS, and what the ledger PRICES, given the analyst's stored
 * override and the live derived cost.
 *
 * Pure on purpose. The bug this replaced was invisible in the callbacks: the box is SEEDED
 * with the derived cost at load, so reading the input string as "the override" made it
 * non-null on every lead — the derived cost was then never used again, and the conversion
 * cost stayed frozen at the count the lead was underwritten at while the analyst dialled
 * beds on and the rent climbed. Same lesson as planRoomRateWrites above.
 *
 * `override` is the STORED value (calculatorInputs.coLivingConversionCost), null when unset.
 */
export const resolveFurnishingLine = ({ override = null, derived = null }) => {
    const d = num(derived, NaN);
    const hasDerived = isFinite(d);
    const o = num(override, NaN);
    const hasOverride = isFinite(o);
    const rounded = hasOverride ? Math.round(o) : null;
    return {
        // What the ledger charges.
        furnishing: hasOverride ? rounded : (hasDerived ? d : 0),
        // What the box displays (null ⇒ nothing to show yet).
        shown: hasOverride ? rounded : (hasDerived ? d : null),
        // True only when the analyst's number actually differs from what we'd derive —
        // drives the "edited" badge + the reset affordance.
        edited: hasOverride && rounded !== d,
    };
};

/**
 * Whether a blur on the conversion-cost box is an EDIT (⇒ write an override) or merely
 * focus (⇒ write nothing). Focus is not an edit: tabbing through the box must never freeze
 * the derived cost in as a permanent override — that would silently pin the cost of today's
 * plan onto a lead whose room count is still moving.
 */
export const planFurnishingWrite = ({ typed = null, override = null, derived = null }) => {
    const { shown } = resolveFurnishingLine({ override, derived });
    const t = num(typed, NaN);
    const next = isFinite(t) ? Math.max(0, Math.round(t)) : null;
    if (next === shown) return { write: false, value: null };
    return { write: true, value: next };
};

/**
 * The co-living repair line. A company's flat post-inspection buffer (inspectionBuffer)
 * REPLACES the AI repair estimate when set (> 0) — Carlos's "$5K for turnkey." Pairs with
 * the condition filter (only turnkey/Lipstick-or-better deals reach the economics). Buffer
 * 0 ⇒ use the AI estimate unchanged. Mirror of functions/utils/coLivingReturns.js.
 *
 * The operator's own figure (`calculatorInputs.coLivingRepairCost`, or the buyer's scratch
 * box on the deal page) outranks BOTH: it bypasses the buffer rather than being replaced by
 * it, because the buffer is a company policy for a house nobody has walked and whoever typed
 * a figure has better information about this one. A genuine 0 is a real value and is taken.
 */
export const resolveCoLivingRepairLine = (aiRepairs, knobs, override = null) => {
    const o = num(override, NaN);
    if (isFinite(o) && o >= 0) return o;
    const buffer = nonNeg(knobs?.inspectionBuffer);
    return buffer > 0 ? buffer : nonNeg(aiRepairs);
};

/**
 * Whether THIS house has a pool we are charging to fill. Reads the flag persisted onto
 * `coLivingInterior.pool` at ingestion (mirrored from verifiedCharacteristics.pool), with an
 * explicit `hasPool` argument taking precedence when the caller holds a fresher value.
 *
 * CONFIRMED-ONLY: `true` charges, anything else does not. A lead underwritten before this
 * shipped carries no `pool` field and prices at $0 until it is re-scored.
 *
 * Mirror of functions/utils/coLivingReturns.js#leadHasPool.
 */
export const leadHasPool = (coLivingInterior, hasPool = null) => {
    if (hasPool === true) return true;
    if (hasPool === false) return false;
    return coLivingInterior?.pool === true;
};

/**
 * The pool-fill line — a THIRD term in conversion cost, alongside repairs and furnishing.
 *
 * 🚨 DELIBERATELY OUTSIDE BOTH EXISTING MECHANISMS. Not inside computeCoLivingFurnishing, which
 * returns 0 unless the company opted into `conversionCostOn` — off for every company by default,
 * so filing the pool there would price it at $0 for essentially everyone. Not inside
 * resolveCoLivingRepairLine either, which a flat `inspectionBuffer` replaces wholesale — a
 * turnkey buffer is a claim about CONDITION, and a pool is not a condition defect.
 *
 * The fill is a LIABILITY decision, not an economics one: a rent-by-the-room house puts unrelated
 * adult strangers on a shared lot, and an open pool is an attractive nuisance the operator carries
 * a duty of care on. So the house model assumes it gets filled, and the money shows up in cash to
 * close. Pool properties used to be excluded from co-living entirely for roughly this reason; they
 * are now analysed and charged instead.
 *
 * Precedence: the operator's own figure (`calculatorInputs.coLivingPoolFillCost`, a genuine 0
 * meaning "I'm keeping it" and taken as such — and the only way to charge a fill on a house whose
 * pool we never confirmed), else the company `poolFillCost` knob on a CONFIRMED pool, else 0.
 *
 * Mirror of functions/utils/coLivingReturns.js#resolvePoolFillCost — change one, change the other.
 */
export const resolvePoolFillCost = ({ coLivingInterior = null, calculatorInputs = {}, knobs = {}, hasPool = null } = {}) => {
    const manual = num(calculatorInputs?.coLivingPoolFillCost, NaN);
    if (isFinite(manual) && manual >= 0) return manual;
    if (!leadHasPool(coLivingInterior, hasPool)) return 0;
    const k = { ...DEFAULT_CO_LIVING_UNDERWRITING, ...knobs };
    return nonNeg(k.poolFillCost);
};

/**
 * Full co-living return model at a given purchase price.
 *
 * @param {Object} p
 * @param {number} p.rooms - projected rentable bedrooms
 * @param {number} p.ratePerRoom - MONTHLY $/room
 * @param {number} p.propertyTaxes - MONTHLY property taxes
 * @param {number} p.insurance - MONTHLY insurance
 * @param {number} p.hoaFees - MONTHLY HOA
 * @param {number} p.purchasePrice - price used for debt + cap-rate basis (null → unlevered/no cap)
 * @param {number} p.conversionCost - one-time rehab + furnishing (+ optional bath), part of basis + cash invested
 * @param {Object} p.knobs - overrides merged over DEFAULT_CO_LIVING_UNDERWRITING
 * @returns {Object} returns model (hasData=false when rooms/rate missing)
 */
// propertyTaxSource / insuranceSource are the winning supplier from processor.js's
// corroboration (see functions/checkSubjectAmenities/carryingCosts.js) — 'zillow' |
// 'realtor' | 'reapi' | 'estimate' | null. Surfaced as the row's `assumption` so a
// wildly-off figure (bad MLS data, a stale supplier) is attributable at a glance
// instead of presenting as an unexplained fact.
// A real Map, not an object literal — `source` is an external string (Firestore),
// and a plain-object lookup would resolve prototype-chain keys like "toString" or
// "constructor" to an inherited function instead of undefined, which React then
// can't render ("Objects are not valid as a React child").
const CARRYING_COST_SOURCE_LABEL = new Map([
    ['zillow', 'Zillow'],
    ['realtor', 'Realtor'],
    ['reapi', 'ReAPI'],
    ['estimate', '0.5%-of-value estimate'],
]);
const carryingCostAssumption = (source) => CARRYING_COST_SOURCE_LABEL.get(source) || undefined;

export const computeCoLivingReturns = ({
    rooms,
    ratePerRoom,
    propertyTaxes = 0,
    insurance = 0,
    hoaFees = 0,
    propertyTaxSource = null,
    insuranceSource = null,
    purchasePrice = null,
    conversionCost = 0,
    knobs = {},
}) => {
    const k = { ...DEFAULT_CO_LIVING_UNDERWRITING, ...knobs };
    const beds = nonNeg(rooms);
    const rate = nonNeg(ratePerRoom);

    if (beds <= 0 || rate <= 0) {
        // No room count yet (e.g. no legible floor plan) → we can't price gross rent,
        // the room-scaled opex, or NOI, and we never fabricate them. But the property's
        // FIXED carrying costs are known regardless of room count, so surface them so
        // the ledger isn't fully blank. The consumer keeps the rent-derived total + NOI
        // as "—". Acquisition costs (down payment, closing, points, cash-to-close),
        // however, depend only on price — NOT rent — so they ride through here too.
        return {
            hasData: false,
            fixedOpexBreakdown: [
                { key: 'propertyTaxes', label: 'Property tax', value: round2(nonNeg(propertyTaxes) * 12), assumption: carryingCostAssumption(propertyTaxSource) },
                { key: 'insurance', label: 'Insurance', value: round2(nonNeg(insurance) * 12), assumption: carryingCostAssumption(insuranceSource) },
                { key: 'hoa', label: 'HOA', value: round2(nonNeg(hoaFees) * 12) },
            ],
            ...computeAcquisition(purchasePrice, conversionCost, k, nonNeg(propertyTaxes) + nonNeg(insurance) + nonNeg(hoaFees)),
        };
    }

    const vacancyPct = nonNeg(k.vacancyPct);
    const platformFeePct = nonNeg(k.platformFeePct);
    const managementFeePct = nonNeg(k.managementFeePct);
    const maintenancePct = nonNeg(k.maintenancePct);

    const grossAnnual = beds * rate * 12;
    const vacancyLoss = grossAnnual * vacancyPct;
    const effectiveGrossAnnual = grossAnnual - vacancyLoss;

    const platformFee = effectiveGrossAnnual * platformFeePct;
    // Third-party property management, a % of collected rent, SEPARATE from the PadSplit
    // marketplace fee (a hands-off operator pays both). Default 0 (self-managed).
    const managementFee = effectiveGrossAnnual * managementFeePct;
    // Two-part tariff: a per-HOUSE service charge (internet, trash, water/sewer base, and the
    // electricity that conditions the shell) plus a per-HEAD volume charge. An empty room stops
    // the second term, never the first — the house is still cooled and the internet still bills.
    //
    // The volume charge follows OCCUPIED rooms, so it carries the same vacancy haircut the rent
    // does: 8 rooms at 10% vacancy meters 7.2 heads, not 8. Until 2026-08-11 it multiplied every
    // bed, so vacancy never reached it — the knob has always been DOCUMENTED as following each
    // occupied room, and it simply didn't.
    //
    // ⚠️ `variableCostPerRoomMonthly` below MUST carry the identical factor, and the reason is
    // easy to get backwards: the pre-2026-08-11 model was NOT self-contradictory. Both figures
    // are per NOMINAL room, and both charged the full per-room rate, so they agreed. Haircutting
    // one and not the other is what breaks it — `computeBreakevenRooms` would then net a
    // nominal-room revenue against a full-occupancy cost. Change the two together or neither.
    // That is why the factor is named ONCE here and reused, rather than written out twice.
    //
    // Clamped at 0 — a >100% vacancy knob must never turn a cost into income.
    const occupancyFactor = Math.max(0, 1 - vacancyPct);
    const occupiedRooms = beds * occupancyFactor;
    const utilities = (nonNeg(k.utilitiesBaseMonthly) + occupiedRooms * nonNeg(k.utilitiesPerRoomMonthly)) * 12;
    const cleaning = nonNeg(k.cleaningLawnPestMonthly) * 12;
    const maintenance = grossAnnual * maintenancePct;
    const propertyTaxesAnnual = nonNeg(propertyTaxes) * 12;
    const insuranceAnnual = nonNeg(insurance) * 12;
    const hoaAnnual = nonNeg(hoaFees) * 12;
    const taxesInsHoaAnnual = propertyTaxesAnnual + insuranceAnnual + hoaAnnual;

    // Taxes, insurance and HOA are listed separately: each has a different provenance
    // (county bill / underwriting default / listing) and a different failure mode when
    // absent, so a combined row can't tell the reader which part it can trust.
    //
    // `assumption` states what each dollar figure came out of. Without it the accordion
    // is eight unexplained numbers: "$2,036/mo" is uncheckable, while "Vacancy · 10% of
    // gross rent" is a claim a reader can agree or disagree with — and disagreeing is the
    // point, since every one of these is a knob. Only the knob-driven lines carry one;
    // taxes / insurance / HOA are measured figures whose provenance is the note under the
    // rows, not a rate.
    //
    // NOT called `basis`: this file already uses that word for the cap-rate/MAO basis and
    // for conversionPremium.basis ('floorPlan' | 'roughEstimate'), and a third meaning on
    // a sibling object is how a reader ends up switching on the wrong one.
    // `assumptionParts` rides alongside every knob-driven row and `assumption` is derived from
    // it, so the sentence a reader sees and the sentence an editor writes into are the same one.
    // The three measured rows (tax / insurance / HOA) carry no parts: their number is a bill,
    // not a rate, so there is nothing in their sentence to edit — they take a dollar input on
    // the value side instead. Cleaning is the third shape: a flat monthly dollar that IS the
    // displayed value, so it too is edited on the value side and states no rate here.
    // ── WHICH ROWS SHOW ONE NUMBER, AND WHICH SHOW TWO ───────────────────────────────────
    // `valueKnob` marks a row whose MONTHLY VALUE *IS* a single knob — nothing is derived, so
    // the figure on the right and the figure you would edit are the same number. Those rows
    // must offer exactly one field (in the value column) or the row prints its own value
    // twice, once as an input and once as text.
    //
    // Cleaning is always such a row: a flat monthly charge. Utilities is one CONDITIONALLY —
    // it is a two-part tariff, and only when the per-room half is zeroed does it collapse to
    // the per-house charge alone. On a company that sets both, $400/house + 7.2 rooms × $60
    // genuinely differs from the $832/mo beside it and both numbers have to be on screen.
    //
    // The rate rows (vacancy, platform, management, maintenance) never qualify: 10% and
    // $1,009/mo are two different facts about the same line.
    //
    // `descriptor` is the row's words with no number in them, for the layout that states the
    // rate in its own column instead of inside a sentence. Only rows whose sentence CONTAINS a
    // number get one: the measured bills keep `assumption` alone, because that string is the
    // real provenance ('Zillow', '0.5%-of-value estimate') and a hand-written stand-in like
    // "county bill" would assert a source the lead may not actually have.
    const utilitiesCollapsed = nonNeg(k?.utilitiesPerRoomMonthly) === 0 && nonNeg(k?.utilitiesBaseMonthly) > 0;

    const vacancyParts = [pctKnob('vacancyPct', vacancyPct), ' of gross rent'];
    const platformParts = [pctKnob('platformFeePct', platformFeePct), ' of collected rent'];
    const managementParts = [pctKnob('managementFeePct', managementFeePct), ' of collected rent'];
    const utilitiesParts = utilitiesAssumptionParts(k, occupiedRooms, beds);
    const maintenanceParts = [pctKnob('maintenancePct', maintenancePct), ' of gross rent'];

    const opexBreakdown = [
        { key: 'vacancy', label: 'Vacancy', value: round2(vacancyLoss), assumption: assumptionText(vacancyParts), assumptionParts: vacancyParts, descriptor: 'of gross rent' },
        // Platform (PadSplit/marketplace) and property management are each a % of rent,
        // and each is shown ONLY when the operator actually pays it (fee > 0). A company
        // that self-manages without a marketplace can zero the platform fee and it drops
        // off the ledger instead of rendering a "$0" line; likewise a hands-off operator
        // adds property management. The default (14% platform, 0 management) is unchanged:
        // platform shows, management doesn't.
        //
        // Both bill on COLLECTED rent (gross less vacancy), and vacancy is a listed row
        // right above — so naming the base is the difference between a reader being able
        // to reproduce the line and not.
        ...(platformFee > 0 ? [{ key: 'platform', label: 'Platform fee', value: round2(platformFee), assumption: assumptionText(platformParts), assumptionParts: platformParts, descriptor: 'of collected rent' }] : []),
        ...(managementFee > 0 ? [{ key: 'management', label: 'Property management', value: round2(managementFee), assumption: assumptionText(managementParts), assumptionParts: managementParts, descriptor: 'of collected rent' }] : []),
        // Both halves of the tariff are named, and a half the operator has zeroed is dropped
        // rather than rendered as "+ $0" — a stated zero reads as a measurement, and these are
        // assumptions. Both zero is the "utilities are on the members" case, which is a real
        // configuration and says so.
        {
            key: 'utilities', label: 'Utilities + internet', value: round2(utilities),
            assumption: assumptionText(utilitiesParts), assumptionParts: utilitiesParts,
            ...(utilitiesCollapsed
                ? { valueKnob: 'utilitiesBaseMonthly', descriptor: 'per house' }
                : { descriptor: null }),
        },
        { key: 'cleaning', label: 'Cleaning / lawn / pest', value: round2(cleaning), assumption: 'Flat, per house', valueKnob: 'cleaningLawnPestMonthly', descriptor: 'flat, per house' },
        { key: 'maintenance', label: 'Maintenance + CapEx', value: round2(maintenance), assumption: assumptionText(maintenanceParts), assumptionParts: maintenanceParts, descriptor: 'of gross rent' },
        { key: 'propertyTaxes', label: 'Property tax', value: round2(propertyTaxesAnnual), assumption: carryingCostAssumption(propertyTaxSource) },
        { key: 'insurance', label: 'Insurance', value: round2(insuranceAnnual), assumption: carryingCostAssumption(insuranceSource) },
        { key: 'hoa', label: 'HOA', value: round2(hoaAnnual) },
    ];

    const totalOpexAnnual = platformFee + managementFee + utilities + cleaning + maintenance + taxesInsHoaAnnual;
    const noiAnnual = round2(effectiveGrossAnnual - totalOpexAnnual);
    const noiMonthly = round2(noiAnnual / 12);

    // ⚠️ `noiMonthly` is the OPERATOR's: net of vacancy, platform fee, management, utilities,
    // cleaning and maintenance. Right for cash flow / CoC / cap rate, WRONG as a DSCR input —
    // a DSCR lender underwrites `gross rent ÷ PITIA` and deducts no operating expense. The
    // sizing cap consumes `grossMonthly` + taxes/insurance/HOA instead. coLiving/MONEY.md "The bridge-refi model".

    // All-in basis = purchase + conversion, so cap rate is the true inverse of the
    // reverse-cap MAO solve (value = NOI / cap ⇒ purchase = NOI/cap − conversion).
    const price = (typeof purchasePrice === 'number' && purchasePrice > 0) ? purchasePrice : null;
    const allInBasis = price != null ? price + nonNeg(conversionCost) : null;
    const capRateAtPrice = allInBasis && allInBasis > 0
        ? Math.round((noiAnnual / allInBasis) * 10000) / 100  // percent, 2dp
        : null;

    // Levered metrics (only when we have a price to finance against). Debt service is
    // NOT an opex line — it sits below NOI, so it never enters opexBreakdown or the
    // cap-rate / MAO solve. The loan terms ride along so the UI can show what the
    // payment is actually built from rather than a bare number.
    const downPayment = Math.min(1, Math.max(0, num(k.downPayment, 0)));
    // Cash-to-close figures depend only on price/conversion — shared with the
    // no-rent early return above so the two paths can never drift.
    const { loanAmount, downPaymentDollars, closingCosts, loanPoints, holdingCost, cashInvested } =
        computeAcquisition(price, conversionCost, k, nonNeg(propertyTaxes) + nonNeg(insurance) + nonNeg(hoaFees));
    let debtServiceMonthly = 0;
    let cashFlowMonthly = null;
    let cashOnCash = null;
    if (price != null) {
        debtServiceMonthly = amortizedMonthlyPayment(loanAmount, k.mortgageRate, k.loanTermYears);
        cashFlowMonthly = round2(noiMonthly - debtServiceMonthly);

        // Cash-on-cash divides by every dollar out of pocket at close (cashInvested),
        // not just the down payment — so closing costs and origination points are in
        // the denominator too. This part genuinely needs rent (cashFlow → NOI).
        cashOnCash = cashInvested > 0
            ? Math.round(((cashFlowMonthly * 12) / cashInvested) * 10000) / 100  // percent, 2dp
            : null;
    }

    // Per-room + fixed decomposition for the breakeven-rooms solve. Maintenance and the
    // VOLUME half of utilities scale with rooms; the utilities SERVICE charge joins cleaning +
    // taxes/ins/HOA + debt on the fixed side.
    //
    // Splitting utilities across the two buckets is the whole point of the two-part tariff:
    // while the entire $130/room sat here, the solve believed an empty room saved $130/mo of
    // utilities. It saves the volume charge alone — the shell is still conditioned and the
    // internet still bills — so breakeven occupancy came out optimistic on every deal.
    //
    // ⚠️ The volume charge carries the SAME (1 − vacancyPct) haircut it carries in NOI, and it
    // has to: `computeBreakevenRooms` nets this against `rate × (1 − vacancyPct) × (1 − feePct)`,
    // which is revenue per NOMINAL room — a room that is let but vacant part of the time — not
    // per room-actually-full. Mixing a nominal-room revenue with a full-occupancy cost understates
    // the net by `utilitiesPerRoomMonthly × vacancyPct` ($6/room/mo on the defaults, $15 at 25%
    // vacancy), which inflates the room count the solve returns — and `ceil` can turn that into a
    // whole extra room. Maintenance needs no such factor: NOI charges it on GROSS rent, so per
    // nominal room it is already the un-haircut `rate × maintenancePct`.
    const variableCostPerRoomMonthly = round2(
        nonNeg(k.utilitiesPerRoomMonthly) * occupancyFactor + rate * maintenancePct
    );
    const fixedMonthlyExDebt = round2(nonNeg(propertyTaxes) + nonNeg(insurance) + nonNeg(hoaFees) + nonNeg(k.cleaningLawnPestMonthly) + nonNeg(k.utilitiesBaseMonthly));

    return {
        hasData: true,
        rooms: beds,
        ratePerRoom: rate,
        grossAnnual: round2(grossAnnual),
        grossMonthly: round2(grossAnnual / 12),
        effectiveGrossAnnual: round2(effectiveGrossAnnual),
        opexBreakdown,
        totalOpexAnnual: round2(totalOpexAnnual),
        noiAnnual,
        noiMonthly,
        capRateAtPrice,
        debtServiceMonthly,
        loanAmount,
        downPaymentPct: downPayment,
        mortgageRate: num(k.mortgageRate, DEFAULT_CO_LIVING_UNDERWRITING.mortgageRate),
        loanTermYears: num(k.loanTermYears, DEFAULT_CO_LIVING_UNDERWRITING.loanTermYears),
        cashFlowMonthly,
        cashInvested,
        downPaymentDollars,
        closingCosts,
        loanPoints,
        holdingCost,
        cashOnCash,
        // intermediates the breakeven solve consumes
        vacancyPct,
        platformFeePct,
        managementFeePct,
        variableCostPerRoomMonthly,
        fixedMonthlyExDebt,
    };
};

/**
 * One-line description of the loan behind the mortgage payment, for the ledger row
 * that would otherwise show a bare number. Lives here, not in the pages, because both
 * the internal ledger and the buyer-facing deal page render it and must never drift —
 * in particular on the all-cash branch, where "$0 at 7.5% over 30 years" is nonsense.
 *
 * @param {Object} returns - a computeCoLivingReturns() result
 * @param {Function} formatMoney - the caller's currency formatter (differs per surface)
 * @returns {string|null} null when there is no priced deal to finance
 */
export const describeCoLivingLoan = (returns, formatMoney) => {
    if (!returns || returns.hasData !== true || returns.loanAmount == null) return null;
    if (!(returns.loanAmount > 0)) return 'All cash — no loan';
    const ratePct = Math.round(num(returns.mortgageRate, 0) * 1000) / 10;
    return `${formatMoney(returns.loanAmount)} at ${ratePct}% · ${returns.loanTermYears} yr fixed`;
};

/**
 * Reverse cap-rate MAO — the ARV-analog offer ceiling. The max all-in basis a buyer
 * can pay and still hit the target cap rate is NOI ÷ targetCapRate; subtract the
 * one-time conversion cost to get the max PURCHASE price.
 *
 * @param {Object} p
 * @param {number} p.noiAnnual
 * @param {number} p.targetCapRate - decimal (0.07)
 * @param {number} p.conversionCost
 * @returns {{ hasData:boolean, mao?:number, incomeValue?:number }}
 */
export const solveCoLivingMao = ({ noiAnnual, targetCapRate, conversionCost = 0 }) => {
    const noi = num(noiAnnual, NaN);
    const cap = num(targetCapRate, NaN);
    if (!isFinite(noi) || !isFinite(cap) || cap <= 0) return { hasData: false };
    const incomeValue = noi / cap;
    const mao = round2(incomeValue - nonNeg(conversionCost));
    return { hasData: true, mao, incomeValue: round2(incomeValue) };
};

/**
 * Breakeven occupancy — how many rooms must stay rented to cover all costs
 * (operating + debt). The co-living risk metric: the gap to the projected room
 * count is the deal's margin of safety.
 *
 * @param {Object} p
 * @param {number} p.ratePerRoom - MONTHLY $/room
 * @param {number} p.vacancyPct - decimal
 * @param {number} p.platformFeePct - decimal
 * @param {number} [p.managementFeePct] - decimal (property management, off collected rent)
 * @param {number} p.variableCostPerRoomMonthly - maintenance + the VOLUME half of utilities, per room
 * @param {number} p.fixedMonthly - taxes + insurance + HOA + cleaning + the utilities SERVICE charge (ex-debt)
 * @param {number} p.debtServiceMonthly
 * @param {number} [p.rooms] - projected rooms, for occupancyPct
 * @returns {{ hasData:boolean, rooms?:number, netPerRoomMonthly?:number, occupancyPct?:number }}
 */
export const computeBreakevenRooms = ({
    ratePerRoom,
    vacancyPct,
    platformFeePct,
    managementFeePct = 0,
    variableCostPerRoomMonthly,
    fixedMonthly,
    debtServiceMonthly,
    rooms = null,
}) => {
    const rate = nonNeg(ratePerRoom);
    // Platform + management are both % of collected rent, taken off the same base, so
    // each rented room nets rate·(1−vacancy)·(1−platform−management) − per-room variable.
    const feePct = Math.min(1, nonNeg(platformFeePct) + nonNeg(managementFeePct));
    const netPerRoomMonthly = round2(
        rate * (1 - nonNeg(vacancyPct)) * (1 - feePct) - nonNeg(variableCostPerRoomMonthly)
    );
    // Each room loses money on its own ⇒ no occupancy breaks even.
    if (netPerRoomMonthly <= 0) return { hasData: false, netPerRoomMonthly };

    const totalFixed = nonNeg(fixedMonthly) + nonNeg(debtServiceMonthly);
    const breakevenRooms = Math.max(0, Math.ceil(totalFixed / netPerRoomMonthly));
    const occupancyPct = (typeof rooms === 'number' && rooms > 0)
        ? Math.round((breakevenRooms / rooms) * 1000) / 10  // percent, 1dp
        : null;

    return { hasData: true, rooms: breakevenRooms, netPerRoomMonthly, occupancyPct };
};
